import { matchPath } from 'react-router-dom';

/**
 * Hệ thống phân quyền theo permission keys (dạng "module.action").
 *
 * - `data.type === 1` ⇒ Super Admin: toàn quyền (không cần check permissions).
 * - Các tài khoản khác: quyền được lấy từ `data.info.role.permissions` khi đăng nhập.
 *
 * Mỗi route được map tới một permission key. Khi user truy cập route,
 * hàm `canAccess` kiểm tra:
 *   1. Nếu super admin ⇒ cho phép.
 *   2. Nếu route không yêu cầu permission (empty string) ⇒ cho phép.
 *   3. Nếu permissions chứa key yêu cầu ⇒ cho phép.
 *   4. Ngược lại ⇒ từ chối.
 *
 * Đây là nguồn khai báo route duy nhất: key là path pattern của route
 * (giống `<Route path>`, có thể chứa tham số như '/customer/:id').
 *   - App.tsx dựng route từ map có kiểu `Record<AppRoutePath, element>`, nên thiếu
 *     một bên (route không có quyền hoặc quyền không có route) là lỗi TypeScript.
 *   - Link trên menu dùng kiểu `MenuUrl`, trỏ tới route không tồn tại cũng là lỗi TypeScript.
 *   - `permissions.test.ts` kiểm tra mọi permission key đều có trong `PERMISSION_MODULES`.
 *
 * Để thêm route mới: thêm vào `ROUTE_PERMISSIONS` với permission key tương ứng
 * ('' nếu chỉ cần đăng nhập), rồi thêm element vào `ROUTE_ELEMENTS` trong App.tsx.
 */

const defineRoutes = <T extends Record<string, string>>(routes: T): T => routes;

export const ROUTE_PERMISSIONS = defineRoutes({
  // Public-protected (đã login là vào được)
  '/unauthorized': '',
  // Trang thử nghiệm Supabase Realtime: đã login là vào được, nhưng KHÔNG
  // dùng làm route mặc định sau khi đăng nhập (xem getDefaultRoute).
  '/realtime-demo': '',
  // Thông báo là dữ liệu cá nhân của người đăng nhập -> không cần quyền riêng
  '/notifications': '',

  // Tổng quan
  '/': 'overview.view',

  // CMS / nghiệp vụ
  '/chats': 'chats.view',
  '/chat/:id': 'chats.view',
  '/customer': 'customer.view',
  '/customer/:id': 'customer.view',
  '/appointments': 'appointment.view',
  '/appointment/create': 'appointment.create',
  '/appointment/detail/:id': 'appointment.view',
  '/appointment/:id': 'appointment.view',
  '/consultations': 'consultation.view',
  '/consultation/detail/:id': 'consultation.view',
  '/execution-schedule': 'execution.view',
  '/execution-schedule/detail/:id': 'execution.view',
  '/list-order': 'list_order.view',
  '/order-detail': 'list_order.view',

  // Chuyên môn
  '/hair-clinic': 'hair_clinic.view',
  '/miko-reception': 'miko_reception.view',
  '/cosmetic-surgery-reception': 'cosmetic_surgery.view',
  '/thread-lift-reception': 'thread_lift.view',
  '/consultation-form': 'ozempic.view',
  '/beauty-consultation': 'beauty_consultation.view',
  '/consultation-tabs': 'skin_consultation.view',

  // Danh mục
  '/customer-tags': 'sale_tags.view',
  // Ma trận chuyển trạng thái cấu hình trên chính các thẻ sale -> dùng chung quyền
  '/appointment-transitions': 'sale_tags.view',
  '/care-status': 'chat_tags.view',
  '/profession': 'profession.view',
  '/quick-messages': 'quick_messages.view',

  // Nhân viên
  '/employee': 'employee.view',
  '/group-roles': 'group_roles.view',
  '/departments': 'departments.view',

  // Dịch vụ & Sản phẩm
  '/services': 'service_categories.view',
  '/services-list': 'services.view',
  '/products-cat': 'product_categories.view',
  '/products': 'products.view',

  // Khác
  '/advertise': 'advertise.view',
  '/advertise/detail': 'advertise.view',
  '/branch-manager': 'branch_manager.view',

  // Báo cáo
  '/overview-report': 'overview_report.view',
  '/page-report': 'page_report.view',
  '/sale-online-report': 'sale_online_report.view',
  '/customer-report': 'customer_report.view',
  '/business-report': 'overview_report.view',
  '/dashbroad-truc-page': 'truc_page.view',

  // Bán hàng POS
  '/pos': 'pos.create',
  '/pos/orders': 'pos.view',
  '/pos/drafts': 'pos.create',
  '/pos/transactions': 'pos.view',
  '/pos/refunds': 'pos.view',
  '/pos/promotions': 'pos.view',
  '/pos/import': 'pos.import',
  '/pos/dashboard': 'pos.view',
  // Điều phối vào phòng: thu nợ + đón khách là thao tác bán hàng
  '/pos/dispatch': 'pos.create',

  // Thẻ liệu trình
  '/treatment': 'treatment.view',
  '/treatment/cards': 'treatment.view',
  '/treatment/checkin': 'treatment.create',
  '/treatment/debts': 'treatment.view',
  '/treatment/packages': 'treatment.view',
  // Tài liệu
  '/personal-documents': 'document.view',
  '/personal-documents/detail': 'document.view',
  '/document-dashboard': 'document.edit',
  '/document-management': 'document.edit',
  '/add-new-document': 'document.create',
  '/document-approval': 'document.approve',
  '/document-trash': 'document.delete',
  '/document-type': 'document.config',
  '/list-of-documents': 'document.config',

  // Cá nhân (đã login là vào được)
  '/admin/profile': '',

  // Form khai báo sau check-in: mở từ kiosk (chỉ có quyền check-in) lẫn từ App Tư vấn
  '/khai-bao': '',

  // Máy check-in tại sảnh
  '/check-in': 'checkin.view',

  // CSKH
  '/care-list': 'care.view',
  '/customer-demo': 'care.view',
  '/customer-demo/detail': 'care.view',
  '/cskh-dashboard': 'care.view',
  '/cskh-tasks': 'care.view',
  '/cskh-complaints': 'care.view',
  '/cskh-surveys': 'care.view',

  // Marketing
  '/marketing-report': 'marketing.view',
  '/marketing-campaign-report': 'marketing.view',
  '/advertising-campaign': 'marketing.view',
  '/advertising-campaign-management': 'marketing.view',

  // Phòng mổ & ê-kíp
  '/or-schedule': 'surgery.view',
  '/operating-room': 'surgery.view',
  '/implementation-schedule': 'surgery.view',
  '/assign': 'surgery.view',

  // Workplace
  '/social': 'social.view',
  '/work-report': 'task.view',
  '/work-list': 'task.view',
  '/work-calender': 'task.view',
  '/projects': 'project.view',
  '/projects/detail': 'project.view',
  '/projects/add': 'project.create',
  '/projects/setup': 'project.config',
  '/request': 'signature.view',
  '/request/add': 'signature.create',
  '/submit-for-signature': 'signature.view',
  '/submit-for-signature/add': 'signature.config',
  '/digital-signature': 'signature.view',
  '/digital-signature/detail': 'signature.view',
  '/assets': 'asset.view',
  '/assets/list': 'asset.view',
  '/assets/detail': 'asset.view',
  '/assets/allocation': 'asset.view',
  '/assets/allocation-detail': 'asset.view',
  '/assets/recall': 'asset.view',
  '/assets/recall-detail': 'asset.view',
  '/assets/add': 'asset.create',
  '/assets/allocation-add': 'asset.create',
  '/assets/add-new-recall': 'asset.create',
  '/assets/edit': 'asset.edit',
  '/assets/recall-edit': 'asset.edit',
  '/assets/setup': 'asset.config',
  '/so-do-to-chuc': 'org.view',
  '/tien-trinh': 'org.view',
  '/work-process-page': 'org.view',

  // HRM
  '/personnel-management': 'personnel.view',
  '/personnel-management/dashbroad': 'personnel.view',
  '/personnel-management/detail': 'personnel.view',
  '/personnel-management/add': 'personnel.create',
  '/probation': 'personnel.view',
  '/offboarding': 'personnel.view',
  '/headcount-plan': 'personnel.view',
  '/employment-contract': 'personnel.view',
  '/employment-contract/detail': 'personnel.view',
  '/employment-contract/add': 'personnel.create',
  '/decision': 'personnel.view',
  '/decision/detail': 'personnel.view',
  '/contract-management': 'personnel.view',
  '/career-progression': 'personnel.view',
  '/recruitment': 'recruitment.view',
  '/candidates-view': 'recruitment.view',
  '/candidate-detail/detail': 'recruitment.view',
  '/jobs-view': 'recruitment.view',
  '/interviews': 'recruitment.view',
  '/offers': 'recruitment.view',
  '/talent-pool': 'recruitment.view',
  '/job/detail': 'recruitment.view',
  '/candidate/add': 'recruitment.create',
  '/job/add': 'recruitment.create',
  '/timesheet': 'timekeeping.view',
  '/timekeeping': 'timekeeping.view',
  '/time-requests': 'timekeeping.view',
  '/leave-balance': 'timekeeping.view',
  '/shifts': 'timekeeping.view',
  '/furlough': 'timekeeping.view',
  '/furlough/add': 'timekeeping.create',
  '/task-assignment': 'timekeeping.assign',
  '/timekeeping-reports': 'timekeeping.view',
  '/timekeeping-reports/bao-cao-don-xin-nghi': 'timekeeping.view',
  '/timekeeping-reports/bao-cao-di-muon': 'timekeeping.view',
  '/timekeeping-reports/bao-cao-tong-hop': 'timekeeping.view',
  '/salary': 'salary.view',
  '/salary/dashboard': 'salary.view',
  '/pit': 'salary.view',
  '/salary-advances': 'salary.view',
  '/payslips': 'salary.view',
  '/payroll-settings': 'salary.view',

  // Kho hàng
  '/warehouse-dashboard': 'warehouse.view',
  '/warehouses': 'warehouse.view',
  '/warehouses/detail': 'warehouse.view',
  '/warehouse-items': 'warehouse.view',
  '/service-norms': 'warehouse.view',
  '/inventory-count': 'warehouse.view',
  '/supplier': 'warehouse.view',
  '/supplier/detail': 'warehouse.view',
  '/supplier/add': 'warehouse.create',
  '/sales-contract': 'warehouse.view',
  '/sales-contract/detail': 'warehouse.view',
  '/sales-contract/add': 'warehouse.create',
  '/purchase-order': 'warehouse.view',
  '/purchase-order/detail': 'warehouse.view',
  '/purchase-order/add': 'warehouse.create',
  '/import-export': 'warehouse.view',
  '/import-export/detail': 'warehouse.view',
  '/import-export/add-import': 'warehouse.create',
  '/import-export/add-export': 'warehouse.create',
  '/import-export/create-inventory-count': 'warehouse.create',
  '/inventory': 'warehouse.view',
  '/inventory/detail': 'warehouse.view',
  '/report-list': 'warehouse.view',

  // Tài chính
  '/revenue': 'finance.view',
  '/expense': 'finance.view',
  '/expense-detail/detail': 'finance.view',
  '/expense/add': 'finance.create',
  '/budget': 'finance.view',
  '/bank-list': 'finance.view',
  '/ke-toan': 'finance.view',
  '/ke-toan/bills': 'finance.view',
  '/ke-toan/reconcile': 'finance.view',
  '/ke-toan/invoices': 'finance.view',
  '/ke-toan/tax-rates': 'finance.view',
  '/ke-toan/tax-return': 'finance.view',
  '/ke-toan/books': 'finance.view',
  '/ke-toan/cards': 'finance.view',
  '/ke-toan/deposits': 'finance.view',
  '/ke-toan/refunds': 'finance.view',
  '/ke-toan/commission': 'finance.view',
  '/ke-toan/counter': 'finance.view',
  '/ke-toan/counter-bills': 'finance.view',
  '/ke-toan/shift-review': 'finance.view',
  '/ke-toan/shift': 'finance.view',
  '/ke-toan/sessions': 'finance.view',
  '/debt': 'finance.view',
  '/debt-detail/detail': 'finance.view',
  '/debt-contract/detail': 'finance.view',

  // Lễ tân
  '/reception-checkin': 'reception.view',
  '/reception-queue': 'reception.view',
  '/reception-booking': 'reception.view',

  // Telesales
  '/telesales/dashboard': 'telesales.view',
  '/telesales/chats': 'chats.view',
  '/telesales/customer': 'telesales.view',
  '/telesales/call-center': 'telesales.view',
  '/telesales/appointments': 'telesales.view',
  '/telesales/consultations': 'telesales.view',
  '/telesales/schedule': 'telesales.view',
  '/telesales/reassign-history': 'telesales.view',
  '/telesales/deposits': 'telesales.view',
  // Cài đặt Telesale: menu nằm ở Quản trị → Cài đặt
  '/telesales/settings': 'telesales.view',

});

/** Path pattern của mọi route được bảo vệ (dùng làm key cho ROUTE_ELEMENTS trong App.tsx). */
export type AppRoutePath = keyof typeof ROUTE_PERMISSIONS;

/** Link hợp lệ trên menu: route đã khai báo, hoặc '/#' cho mục chưa có trang (bị ẩn). */
export type MenuUrl = AppRoutePath | '/#';

const ROUTE_TABLE: Record<string, string> = ROUTE_PERMISSIONS;
const PARAM_PATTERNS = Object.keys(ROUTE_TABLE).filter((p) => p.includes(':'));

/**
 * Lấy permission key yêu cầu cho 1 pathname: ưu tiên route tĩnh khớp chính xác,
 * sau đó tới route có tham số (vd '/customer/:id').
 * Trả về `null` nếu route không nằm trong bảng (mặc định: từ chối).
 */
export function getRequiredPermission(pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (Object.prototype.hasOwnProperty.call(ROUTE_TABLE, path)) return ROUTE_TABLE[path];
  const pattern = PARAM_PATTERNS.find((p) => matchPath(p, path));
  return pattern ? ROUTE_TABLE[pattern] : null;
}

/**
 * Kiểm tra quyền truy cập 1 pathname.
 */
export function canAccess(
  pathname: string,
  permissions: string[] = [],
  isSuperAdmin = false
): boolean {
  if (isSuperAdmin) return true;
  const required = getRequiredPermission(pathname);
  if (required === null) return false;
  if (required === '') return true;
  return permissions.includes(required);
}

/**
 * Route mặc định sau khi đăng nhập. Super admin về '/', còn lại vào
 * route đầu tiên trong `ROUTE_PERMISSIONS` mà user có quyền.
 * Màn check-in xếp cuối: chỉ là trang mặc định của tài khoản máy check-in (không có quyền màn nào khác).
 */
export function getDefaultRoute(
  permissions: string[] = [],
  isSuperAdmin = false
): string {
  if (isSuperAdmin) return '/';
  // Không ưu tiên chuyển hướng tới các trang thử nghiệm/không phải nghiệp vụ
  const EXCLUDED_DEFAULT_ROUTES = ['/unauthorized', '/realtime-demo', '/notifications', '/admin/profile', '/khai-bao', '/check-in'];
  const candidates = Object.keys(ROUTE_PERMISSIONS).filter(
    (p) => p !== '/' && !EXCLUDED_DEFAULT_ROUTES.includes(p) && !p.includes(':')
  );
  for (const route of [...candidates, '/check-in']) {
    if (canAccess(route, permissions, isSuperAdmin)) return route;
  }
  return '/unauthorized';
}

/** Helper: kiểm tra user có 1 permission key cụ thể không. */
export function hasPermission(
  permissions: string[],
  key: string,
  isSuperAdmin = false
): boolean {
  if (isSuperAdmin) return true;
  if (!key) return true;
  return permissions.includes(key);
}

